// Change Lane Assignment Modal Component
import React, { useState, useEffect } from 'react';
import { Button, LoadingSpinner } from '../Common';
import { useApi } from '../../hooks/useApi';
import { LaneType } from '../../types';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface LaneGridData {
  laneAssignmentId: number;
  checkpointId: number;
  laneTypeId: number;
  laneTypeName: string;
  laneTypeDescription: string;
  laneTypePurpose?: string;
  isOpen: boolean;
  assetGroupsCount: number;
  openAssets: number;
  totalAssets: number;
  last_updated: string;
  updated_by: string;
}

interface ChangeLaneAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  lane: LaneGridData;
  checkpointId: number;
  onSuccess: () => void;
}

export const ChangeLaneAssignmentModal: React.FC<ChangeLaneAssignmentModalProps> = ({
  isOpen,
  onClose,
  lane,
  checkpointId,
  onSuccess
}) => {
  const api = useApi(); // Use authenticated API service
  const [availableLaneTypes, setAvailableLaneTypes] = useState<LaneType[]>([]);
  const [selectedLaneTypeId, setSelectedLaneTypeId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingLaneTypes, setLoadingLaneTypes] = useState(false);

  // Load available lane types when modal opens
  useEffect(() => {
    if (isOpen && checkpointId) {
      loadLaneTypes();
    }
  }, [isOpen, checkpointId]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedLaneTypeId('');
      setReason('');
    }
  }, [isOpen]);

  const loadLaneTypes = async () => {
    try {
      setLoadingLaneTypes(true);
      const laneTypes = await api.getLaneTypes(checkpointId);
      
      // Filter out the current lane type AND only show lane types where purpose = "APP"
      const availableTypes = laneTypes.filter(
        laneType => laneType.lane_type_id !== lane.laneTypeId && 
                   laneType.purpose === "APP"
      );
      
      setAvailableLaneTypes(availableTypes);
    } catch (error) {
      console.error('Error loading lane types:', error);
      toast.error('Failed to load available lane types');
    } finally {
      setLoadingLaneTypes(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedLaneTypeId) {
      toast.error('Please select a new lane type');
      return;
    }

    try {
      setLoading(true);
      
      const result = await api.changeLaneAssignmentType(
        lane.laneAssignmentId,
        parseInt(selectedLaneTypeId),
        reason.trim() || undefined
      );

      if (result.success) {
        toast.success(result.message || 'Lane assignment type changed successfully');
        onSuccess();
        onClose();
      } else {
        toast.error(result.message || 'Failed to change lane assignment type');
      }
    } catch (error) {
      toast.error('Failed to change lane assignment type');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedLaneTypeId('');
    setReason('');
    onClose();
  };

  if (!isOpen) return null;

  const selectedLaneType = availableLaneTypes.find(
    lt => lt.lane_type_id === parseInt(selectedLaneTypeId)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Change Queue Assignment Type</h3>
          <button
            onClick={handleCancel}
            disabled={loading}
            className="text-gray-400 hover:text-white disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Current Lane Info */}
        <div className="bg-gray-700 rounded-md p-3 mb-4">
          <p className="text-sm text-gray-300 mb-1">
            <span className="font-medium">Current:</span> {lane.laneTypeName}
          </p>
          <p className="text-sm text-gray-300 mb-1">
            <span className="font-medium">Lane ID:</span> {lane.laneAssignmentId}
          </p>
          <p className="text-sm text-gray-300">
            <span className="font-medium">Asset Groups:</span> {lane.assetGroupsCount}
          </p>
        </div>

        <div className="space-y-4">
          {/* New Lane Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New Lane Type *
            </label>
            {loadingLaneTypes ? (
              <div className="flex items-center justify-center py-3">
                <LoadingSpinner size="sm" />
                <span className="ml-2 text-gray-400">Loading lane types...</span>
              </div>
            ) : (
              <select
                value={selectedLaneTypeId}
                onChange={(e) => setSelectedLaneTypeId(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">Select new lane type</option>
                {availableLaneTypes.map((laneType) => (
                  <option key={laneType.lane_type_id} value={laneType.lane_type_id}>
                    {laneType.type_name}
                    {laneType.description && ` - ${laneType.description}`}
                  </option>
                ))}
              </select>
            )}
            
            {availableLaneTypes.length === 0 && !loadingLaneTypes && (
              <p className="text-sm text-gray-400 mt-1">
                No other APP lane types available for this checkpoint
              </p>
            )}
          </div>

          {/* Selected Lane Type Preview */}
          {selectedLaneType && (
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-md p-3">
              <p className="text-sm text-blue-300 font-medium">{selectedLaneType.type_name}</p>
              {selectedLaneType.description && (
                <p className="text-sm text-blue-400 mt-1">{selectedLaneType.description}</p>
              )}
              {selectedLaneType.purpose && (
                <p className="text-xs text-blue-500 mt-1">Purpose: {selectedLaneType.purpose}</p>
              )}
            </div>
          )}

          {/* Reason Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              placeholder="Enter reason for lane type change..."
              rows={3}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 resize-none"
            />
          </div>

          {/* Cascade Info */}
          <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-md p-3">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-yellow-300 font-medium">Cascade Enabled</p>
                <p className="text-sm text-yellow-400 mt-1">
                  This change will automatically update all {lane.assetGroupsCount} associated asset groups.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex space-x-3">
          <Button
            onClick={handleCancel}
            variant="secondary"
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            className="flex-1"
            disabled={loading || !selectedLaneTypeId || loadingLaneTypes}
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Changing...
              </>
            ) : (
              'Change Type'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};